import { ApiResponse } from "src/models";
import { apiSlice } from "src/store/apiSlice";

export type AwardTier =
  | "presidential"
  | "national"
  | "participation"
  | "incomplete";
export interface TierCounts {
  presidential: number;
  national: number;
  participation: number;
  incomplete: number;
}
export interface ReportRange {
  schoolYear?: string;
  from?: string;
  to?: string;
}
export interface ClassReport {
  classId: string;
  className: string;
  schoolId: string;
  enrolled: number;
  tested: number;
  completionByCategory: { core: number; cardio: number; upper: number };
  tiers: TierCounts;
  modificationsUsed: Record<string, number>;
  windowChanges: {
    studentId: string;
    from: { date: string; tier: AwardTier | null };
    to: { date: string; tier: AwardTier | null };
  }[];
}
export interface ScopeReport {
  scope: "school" | "district";
  id: string;
  name: string;
  enrolled: number;
  tested: number;
  participationRate: number;
  tiers: TierCounts;
  tiersByGrade: Record<string, TierCounts>;
  tiersBySex: Record<string, TierCounts>;
  sessions: number;
  sessionsBySchool?: Record<string, number>;
  activeTeachers: number;
}
export interface ChallengeSummary {
  districtId: string;
  districtName: string;
  range: ReportRange;
  schoolsParticipating: number;
  schoolsTotal: number;
  studentsEnrolled: number;
  studentsTested: number;
  participationRate: number;
  sessionsRun: number;
  teachersActive: number;
  tiers: TierCounts;
  perSchool: {
    schoolId: string;
    name: string;
    enrolled: number;
    tested: number;
    participationRate: number;
    sessions: number;
    tiers: TierCounts;
  }[];
  generatedAt: string;
}

const base = "/api/reporting/v1/reports";
const qs = (range: ReportRange = {}) => {
  const p = new URLSearchParams();
  if (range.schoolYear) p.set("schoolYear", range.schoolYear);
  if (range.from) p.set("from", range.from);
  if (range.to) p.set("to", range.to);
  const s = p.toString();
  return s ? `?${s}` : "";
};

/** Rollups come from the reporting read model: counts and own-change only, never ranks or percentiles. */
export const reportsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getClassReport: builder.query<
      ClassReport,
      { classId: string; range?: ReportRange }
    >({
      query: ({ classId, range }) => ({
        url: `${base}/classes/${classId}${qs(range)}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ report: ClassReport }>) =>
        r.data.report,
      providesTags: ["Reports"],
    }),
    getClassScorecardExport: builder.query<
      { url: string; rows: number },
      { classId: string; range?: ReportRange }
    >({
      query: ({ classId, range }) => ({
        url: `${base}/classes/${classId}/scorecard${qs(range)}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ url: string; rows: number }>) =>
        r.data,
    }),
    getSchoolReport: builder.query<
      ScopeReport,
      { schoolId: string; range?: ReportRange }
    >({
      query: ({ schoolId, range }) => ({
        url: `${base}/schools/${encodeURIComponent(schoolId)}${qs(range)}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ report: ScopeReport }>) =>
        r.data.report,
      providesTags: ["Reports"],
    }),
    getDistrictReport: builder.query<
      ScopeReport,
      { districtId: string; range?: ReportRange }
    >({
      query: ({ districtId, range }) => ({
        url: `${base}/districts/${encodeURIComponent(districtId)}${qs(range)}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ report: ScopeReport }>) =>
        r.data.report,
      providesTags: ["Reports"],
    }),
    getChallengeSummary: builder.query<
      { summary: ChallengeSummary; exportUrl: string },
      { districtId: string; range?: ReportRange }
    >({
      query: ({ districtId, range }) => ({
        url: `${base}/districts/${encodeURIComponent(districtId)}/challenge-summary${qs(range)}`,
        method: "GET",
      }),
      transformResponse: (
        r: ApiResponse<{ summary: ChallengeSummary; exportUrl: string }>,
      ) => r.data,
    }),
  }),
});

export const {
  useGetClassReportQuery,
  useLazyGetClassScorecardExportQuery,
  useGetSchoolReportQuery,
  useGetDistrictReportQuery,
  useLazyGetChallengeSummaryQuery,
} = reportsApiSlice;
